import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import api from "./api";
import {handleResponseStatus, updateIdField, updateItem} from "./utils";

const initialState = {
    items: [],
    orderSummary: {},
    customer: {},
    fetchStatus: "idle",// 'idle' | 'loading' | 'success' | 'failed'
    fetchCustomerStatus: "idle",// 'idle' | 'loading' | 'success' | 'failed'
    fetchOrderSummaryStatus: "idle",// 'idle' | 'loading' | 'success' | 'failed'
    fetchByIdStatus: "idle",// 'idle' | 'loading' | 'success' | 'failed'
    createStatus: "idle",// 'idle' | 'loading' | 'success' | 'failed'
    updateStatus: "idle",// 'idle' | 'loading' | 'success' | 'failed'
    updatePaymentStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    deleteStatus: "idle",// 'idle' | 'loading' | 'success' | 'failed'
    error: null
};

export const fetchOrders = createAsyncThunk('order/fetchOrders', async ({roleSign, scopeId}) => {
    try {
        const response = await api.get(`/${roleSign}/order?id=${scopeId}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const fetchOrderById = createAsyncThunk('order/fetchOrderById', async ({roleSign, orderId}) => {
    try {
        const response = await api.get(`/${roleSign}/order/${orderId}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});


export const fetchCustomer = createAsyncThunk('order/fetchCustomer', async ({phone, roleSign}) => {
    try {
        const response = await api.get(`/${roleSign}/customer/${phone}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const fetchOrderSummary = createAsyncThunk('order/fetchOrderSummary', async ({scopeId, roleSign, scope}) => {
    try {
        let response;
        if (scope === "Restaurant") {
            response = await api.get(`/${roleSign}/restaurant/dashboard?id=${scopeId}`);
        } else {
            response = await api.get(`/${roleSign}/order/orderSummary?id=${scopeId}`);
        }
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const createOrder = createAsyncThunk('order/createOrder', async ({data, roleSign, scopeId}) => {
    const updatedData = {
        id: scopeId,
        ...data,
    };
    try {
        const response = await api.post(`/${roleSign}/order`, updatedData);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const updateOrder = createAsyncThunk('order/updateOrder', async ({data, orderId, roleSign}) => {
    try {
        const response = await api.patch(`/${roleSign}/order/${orderId}`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const updateOrderPayment = createAsyncThunk('order/updateOrderPayment', async ({data, roleSign}) => {
    try {
        const response = await api.patch(`/${roleSign}/order/updateStatus/${data._id}`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});


export const fetchOrdersByFilter = createAsyncThunk('order/fetchOrdersByFilter', async ({
                                                                                            scopeId,
                                                                                            roleSign,
                                                                                            queryString
                                                                                        }) => {
    try {
        const response = await api.get(`/${roleSign}/order/filteredOrders?${queryString}&id=${scopeId}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

const orderSlice = createSlice({
    name: "order",
    initialState,
    reducers: {
        resetCreateStatus(state) {
            state.createStatus = 'idle';
        },
        resetUpdateStatus(state) {
            state.updateStatus = 'idle';
        },
        resetUpdatePaymentStatus(state) {
            state.updatePaymentStatus = 'idle';
        },
        resetFetchCustomerStatus(state) {
            state.fetchCustomerStatus = 'idle';
            state.customer = {};
        },
    },
    extraReducers: (builder) => {
        builder
            // fetch all orders
            .addCase(fetchOrders.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchOrders.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, 'fetchStatus');
                state.items = fetchStatus === "success" ? updateIdField(action.payload.data.data) : state.items;
                state.fetchStatus = fetchStatus;
                state.error = error;
            })
            .addCase(fetchOrders.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })
            // fetch Customer
            .addCase(fetchCustomer.pending, (state) => {
                state.fetchCustomerStatus = 'loading';
            })
            .addCase(fetchCustomer.fulfilled, (state, action) => {
                const {fetchCustomerStatus, error} = handleResponseStatus(action.payload, 'fetchCustomerStatus');
                state.customer = fetchCustomerStatus === "success" ? action.payload.data.data : state.customer;
                state.fetchCustomerStatus = fetchCustomerStatus;
                state.error = error;
            })
            .addCase(fetchCustomer.rejected, (state, action) => {
                state.fetchCustomerStatus = 'failed';
                state.error = action.error.message;
            })

            // fetch  Order Summary
            .addCase(fetchOrderSummary.pending, (state) => {
                state.fetchOrderSummaryStatus = 'loading';
            })
            .addCase(fetchOrderSummary.fulfilled, (state, action) => {
                const {
                    fetchOrderSummaryStatus,
                    error
                } = handleResponseStatus(action.payload, 'fetchOrderSummaryStatus');
                state.orderSummary = fetchOrderSummaryStatus === "success" ? action.payload.data.data : {};
                state.fetchOrderSummaryStatus = fetchOrderSummaryStatus;
                state.error = error;
            })
            .addCase(fetchOrderSummary.rejected, (state, action) => {
                state.fetchOrderSummaryStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch Order by ID
            .addCase(fetchOrderById.pending, (state) => {
                state.fetchByIdStatus = 'loading';
            })
            .addCase(fetchOrderById.fulfilled, (state, action) => {
                const {fetchByIdStatus, error} = handleResponseStatus(action.payload, 'fetchByIdStatus');
                state.items = fetchByIdStatus === "success" ? updateItem(action.payload.data.data, state.items) : state.items;
                state.fetchByIdStatus = fetchByIdStatus;
                state.error = error;
            })
            .addCase(fetchOrderById.rejected, (state, action) => {
                state.fetchByIdStatus = 'failed';
                state.error = action.error.message;
            })


            // create all orders
            .addCase(createOrder.pending, (state) => {
                state.createStatus = 'loading';
            })
            .addCase(createOrder.fulfilled, (state, action) => {
                const {createStatus, error} = handleResponseStatus(action.payload, 'createStatus');
                state.createStatus = createStatus;
                state.error = error;
            })
            .addCase(createOrder.rejected, (state, action) => {
                state.createStatus = 'failed';
                state.error = action.error.message;
            })


            // update order
            .addCase(updateOrder.pending, (state) => {
                state.updateStatus = 'loading';
            })
            .addCase(updateOrder.fulfilled, (state, action) => {
                const {updateStatus, error} = handleResponseStatus(action.payload, 'updateStatus');
                state.updateStatus = updateStatus;
                state.error = error;
            })
            .addCase(updateOrder.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.error.message;
            })

            // update Order Payment
            .addCase(updateOrderPayment.pending, (state) => {
                state.updatePaymentStatus = 'loading';
            })
            .addCase(updateOrderPayment.fulfilled, (state, action) => {
                const {updatePaymentStatus, error} = handleResponseStatus(action.payload, 'updatePaymentStatus');
                state.updatePaymentStatus = updatePaymentStatus;
                state.error = error;
            })
            .addCase(updateOrderPayment.rejected, (state, action) => {
                state.updatePaymentStatus = 'failed';
                state.error = action.error.message;
            })

            // fetch orders using filter
            .addCase(fetchOrdersByFilter.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchOrdersByFilter.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, 'fetchStatus');
                state.items = fetchStatus === "success" ? updateIdField(action.payload.data.data) : state.items;
                state.fetchStatus = fetchStatus;
                state.error = error;
            })
            .addCase(fetchOrdersByFilter.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })
    }
});
export const {
    resetCreateStatus,
    resetUpdateStatus,
    resetUpdatePaymentStatus,
    resetFetchCustomerStatus
} = orderSlice.actions;
export default orderSlice.reducer;

